import { shiftIsoDate } from "./agendaDateFormat";
import { clampAgendaScheduleDate } from "./agendaWeekDateRange";
import type { AgendaDayPayload, AgendaFiltersState, AgendaWeekSummary } from "./agendaTypes";

/**
 * In-memory cache of agenda days for the current tab. Revisiting a day shows it at
 * once and refreshes it in the background; any change on the agenda clears it.
 */
const MAX_CACHED_DAYS = 30;

const dayCache = new Map<string, AgendaDayPayload>();
const weekCache = new Map<string, AgendaWeekSummary>();

export function buildAgendaDayCacheKey(
  userId: number,
  filters: Pick<AgendaFiltersState, "date" | "teamId" | "city" | "consultantId" | "visitType">
): string {
  return JSON.stringify([
    userId,
    filters.date,
    filters.teamId ?? null,
    filters.city ?? null,
    filters.consultantId ?? null,
    filters.visitType ?? "all",
  ]);
}

export function buildAgendaWeekCacheKey(userId: number, teamId: number | undefined, todayIso: string): string {
  return JSON.stringify([userId, teamId ?? null, todayIso]);
}

export function getCachedAgendaDay(key: string): AgendaDayPayload | undefined {
  const value = dayCache.get(key);
  if (value) {
    // Re-insert to keep the most recent entries when trimming.
    dayCache.delete(key);
    dayCache.set(key, value);
  }
  return value;
}

export function setCachedAgendaDay(key: string, value: AgendaDayPayload): void {
  dayCache.delete(key);
  dayCache.set(key, value);
  while (dayCache.size > MAX_CACHED_DAYS) {
    const oldest = dayCache.keys().next().value;
    if (oldest === undefined) break;
    dayCache.delete(oldest);
  }
}

export function hasCachedAgendaDay(key: string): boolean {
  return dayCache.has(key);
}

export function getCachedAgendaWeek(key: string): AgendaWeekSummary | undefined {
  return weekCache.get(key);
}

export function setCachedAgendaWeek(key: string, value: AgendaWeekSummary): void {
  weekCache.set(key, value);
}

/** After a lock, booking or outcome change every cached day may be outdated. */
export function clearAgendaCache(): void {
  dayCache.clear();
  weekCache.clear();
}

/** Previous and next day that the agenda allows, for prefetching. */
export function adjacentAgendaDates(dateIso: string, todayIso: string): string[] {
  return [shiftIsoDate(dateIso, -1), shiftIsoDate(dateIso, 1)].filter(
    (candidate) => clampAgendaScheduleDate(candidate, todayIso) === candidate && candidate !== dateIso
  );
}
